import { SnapshotLabelHtmlClassNames } from "@/animation/video-generator";
import DateRangeIcon from "@/assets/icons/DateRange.module.svg";
import { useDisplayTime, useTimezone } from "@/models/time-control/timeStateHooks";
import { timezones } from "@/overlay/components/DatetimeSelectionWindow/TimeSettings/TimeZoneConst";
import { getCanvasSvgIconImage, preloadCanvasSvgIconImage } from "@/overlay/components/QuickAccessLabel/canvasSvgIcon";
import {
  DATE_AND_TIME_FORMAT_WITH_WEEKDAY,
  normalizeDateString,
} from "@/overlay/components/TimeSettingsButtons/formats";
import { DEFAULT_MAP_TITLE_STYLE } from "@/overlay/overlays/default/mapTitleStyle";
import { useMapInitDates } from "@/overlay/overlays/default/useMapInitDates";
import { useLingui } from "@lingui/react";
import type { JSONInitDate } from "@mm/api.meteomatics.com";
import type { CartographicMapOutput, ElementStyle } from "@mm/metx-workbench.meteomatics.com";
import { DateTime, Duration } from "luxon";
import { useLayoutEffect, useRef } from "react";

const LABEL_OUTER_PADDING_X = 8;
const LABEL_OUTER_PADDING_Y = 6;
const LABEL_ROW_GAP = 4;
const LABEL_CELL_PADDING_X = 12;
const LABEL_CELL_PADDING_Y = 4;
const LABEL_BORDER_RADIUS = 4;
const LABEL_BORDER_COLOR = "#C9CDD2";
const LABEL_BACKGROUND = "rgba(255, 255, 255, 0.60)";
const LABEL_CELL_BACKGROUND = "#FFFFFF";
const LABEL_HEADER_COLOR = "#B0B0B0";
const DIVIDER_COLOR = "#B0B0B0";
const LABEL_TOP_INSET = 4;
const LABEL_RIGHT_INSET = 8;
const LABEL_ICON_GAP = 6;

type MapRecordingLabelCanvasProps = {
  map: CartographicMapOutput;
};

type TableLayout = {
  parameterWidth: number;
  modelWidth: number;
  runWidth: number;
  width: number;
  height: number;
  rowHeight: number;
};

function buildFont(style: ElementStyle | null | undefined, fontSize: number) {
  const fontStyle = style?.fontStyle ?? "normal";
  const fontWeight = style?.fontWeight ?? 400;
  return `${fontStyle} ${fontWeight} ${fontSize}px sans-serif`;
}

function drawRoundedRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  fillStyle: string,
  strokeStyle?: string,
) {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
  ctx.fillStyle = fillStyle;
  ctx.fill();
  if (strokeStyle) {
    ctx.strokeStyle = strokeStyle;
    ctx.stroke();
  }
}

function drawCalendarIconFallback(
  ctx: CanvasRenderingContext2D,
  x: number,
  centerY: number,
  size: number,
  color: string,
) {
  const top = centerY - size / 2;
  const bodyX = x + size * 0.08;
  const bodyY = top + size * 0.16;
  const bodyWidth = size * 0.84;
  const bodyHeight = size * 0.72;
  const bodyRadius = size * 0.12;

  ctx.save();
  ctx.strokeStyle = color;
  ctx.lineWidth = Math.max(1, size * 0.1);

  drawRoundedRect(ctx, bodyX, bodyY, bodyWidth, bodyHeight, bodyRadius, "rgba(0, 0, 0, 0)", color);

  ctx.beginPath();
  ctx.moveTo(bodyX, bodyY + size * 0.2);
  ctx.lineTo(bodyX + bodyWidth, bodyY + size * 0.2);
  ctx.moveTo(bodyX + size * 0.18, top + size * 0.08);
  ctx.lineTo(bodyX + size * 0.18, bodyY + size * 0.16);
  ctx.moveTo(bodyX + bodyWidth - size * 0.18, top + size * 0.08);
  ctx.lineTo(bodyX + bodyWidth - size * 0.18, bodyY + size * 0.16);
  ctx.stroke();
  ctx.restore();
}

function formatOffsetLabel(offsetMinutes: number) {
  if (!offsetMinutes) {
    return "";
  }

  const symbol = offsetMinutes > 0 ? "+" : "-";
  const unit = offsetMinutes % 60 === 0 ? "h" : "m";
  const displayNumber = offsetMinutes % 60 === 0 ? Math.abs(offsetMinutes / 60) : Math.abs(offsetMinutes);
  return `${symbol}${displayNumber}${unit}`;
}

function formatDateLabel(displayTime: DateTime, timezone: string, locale: string) {
  return normalizeDateString(
    displayTime.setZone(timezone).setLocale(locale).toLocaleString(DATE_AND_TIME_FORMAT_WITH_WEEKDAY),
  );
}

function formatInitDateLabel(initDate: JSONInitDate, locale: string) {
  return `${formatDateLabel(DateTime.fromISO(initDate.initDate.toString()), "utc", locale)} UTC`;
}

function measureTableLayout(
  ctx: CanvasRenderingContext2D,
  allInitDates: JSONInitDate[],
  titleStyle: ElementStyle,
  locale: string,
) {
  if (allInitDates.length === 0) {
    return null;
  }

  const fontSize = Math.max(10, (titleStyle.fontSize ?? 15) - 3);
  ctx.font = buildFont(titleStyle, fontSize);

  const parameterWidth = Math.max(
    ctx.measureText("Parameter").width,
    ...allInitDates.map((item) => ctx.measureText(item.parameter ?? "").width),
  );
  const modelWidth = Math.max(
    ctx.measureText("Model").width,
    ...allInitDates.map((item) => ctx.measureText(item.model ?? "").width),
  );
  const runWidth = Math.max(
    ctx.measureText("Run").width,
    ...allInitDates.map((item) => ctx.measureText(formatInitDateLabel(item, locale)).width),
  );

  const rowHeight = fontSize + 8;
  const width =
    LABEL_CELL_PADDING_X * 2 +
    parameterWidth +
    LABEL_CELL_PADDING_X +
    1 +
    LABEL_CELL_PADDING_X +
    modelWidth +
    LABEL_CELL_PADDING_X +
    1 +
    LABEL_CELL_PADDING_X +
    runWidth +
    LABEL_CELL_PADDING_X;
  const height = LABEL_CELL_PADDING_Y * 2 + rowHeight * (allInitDates.length + 1);

  return {
    parameterWidth,
    modelWidth,
    runWidth,
    width,
    height,
    rowHeight,
  } satisfies TableLayout;
}

function drawTable(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  layout: TableLayout,
  allInitDates: JSONInitDate[],
  titleStyle: ElementStyle,
  locale: string,
) {
  const fontSize = Math.max(10, (titleStyle.fontSize ?? 15) - 3);
  const dividerX1 = x + LABEL_CELL_PADDING_X + layout.parameterWidth + LABEL_CELL_PADDING_X;
  const dividerX2 =
    dividerX1 + LABEL_CELL_PADDING_X + 1 + LABEL_CELL_PADDING_X + layout.modelWidth + LABEL_CELL_PADDING_X;

  drawRoundedRect(
    ctx,
    x,
    y,
    layout.width,
    layout.height,
    LABEL_BORDER_RADIUS,
    LABEL_CELL_BACKGROUND,
    LABEL_BORDER_COLOR,
  );

  ctx.save();
  ctx.font = buildFont(titleStyle, fontSize);
  ctx.textBaseline = "middle";
  ctx.fillStyle = LABEL_HEADER_COLOR;
  const headerY = y + LABEL_CELL_PADDING_Y + layout.rowHeight / 2;
  ctx.fillText("Parameter", x + LABEL_CELL_PADDING_X, headerY);
  ctx.fillText("Model", dividerX1 + LABEL_CELL_PADDING_X + 1 + LABEL_CELL_PADDING_X, headerY);
  ctx.fillText("Run", dividerX2 + LABEL_CELL_PADDING_X + 1 + LABEL_CELL_PADDING_X, headerY);

  ctx.strokeStyle = DIVIDER_COLOR;
  ctx.beginPath();
  ctx.moveTo(dividerX1, y + LABEL_CELL_PADDING_Y);
  ctx.lineTo(dividerX1, y + layout.height - LABEL_CELL_PADDING_Y);
  ctx.moveTo(dividerX2, y + LABEL_CELL_PADDING_Y);
  ctx.lineTo(dividerX2, y + layout.height - LABEL_CELL_PADDING_Y);
  ctx.stroke();

  ctx.fillStyle = titleStyle.color ?? "#000000";
  allInitDates.forEach((item, index) => {
    const rowY = y + LABEL_CELL_PADDING_Y + layout.rowHeight * (index + 1) + layout.rowHeight / 2;
    ctx.fillText(item.parameter ?? "", x + LABEL_CELL_PADDING_X, rowY);
    ctx.fillText(item.model ?? "", dividerX1 + LABEL_CELL_PADDING_X + 1 + LABEL_CELL_PADDING_X, rowY);
    ctx.fillText(formatInitDateLabel(item, locale), dividerX2 + LABEL_CELL_PADDING_X + 1 + LABEL_CELL_PADDING_X, rowY);
  });
  ctx.restore();
}

export function MapRecordingLabelCanvas({ map }: MapRecordingLabelCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const displayTime = useDisplayTime();
  const timezone = useTimezone();
  const { i18n } = useLingui();
  const titleStyle = map.titleStyle ?? DEFAULT_MAP_TITLE_STYLE;
  const allInitDates = useMapInitDates(map.layers, displayTime, map.time_offset_mins);

  useLayoutEffect(() => {
    let cancelled = false;
    const canvas = canvasRef.current;
    if (!(canvas && displayTime)) {
      return;
    }

    const measurementCanvas = document.createElement("canvas");
    const measurementContext = measurementCanvas.getContext("2d");
    if (!measurementContext) {
      return;
    }

    const titleFontSize = titleStyle.fontSize ?? 14;
    const labelTime = displayTime.plus(Duration.fromObject({ minutes: map.time_offset_mins }));
    const titleText = map.title || "New Map";
    const timeZoneLabel = typeof timezones[timezone] === "function" ? timezones[timezone]() : String(timezone);
    const dateText = formatDateLabel(labelTime, timezone, i18n.locale);
    const offsetText = formatOffsetLabel(map.time_offset_mins);
    const combinedDateText = offsetText ? `${offsetText} ${dateText} ${timeZoneLabel}` : `${dateText} ${timeZoneLabel}`;
    const iconColor = titleStyle.color ?? "#000000";
    const iconSize = Math.max(12, Math.round(titleFontSize));
    const iconSlotWidth = iconSize + LABEL_ICON_GAP;

    measurementContext.font = buildFont(titleStyle, titleFontSize);
    const titleWidth = Math.ceil(measurementContext.measureText(titleText).width) + LABEL_CELL_PADDING_X * 2;
    const dateWidth =
      Math.ceil(measurementContext.measureText(combinedDateText).width) + LABEL_CELL_PADDING_X * 2 + iconSlotWidth;
    const rowHeight = titleFontSize + LABEL_CELL_PADDING_Y * 2 + 4;
    const tableLayout = measureTableLayout(measurementContext, allInitDates, titleStyle, i18n.locale);
    const contentWidth = titleWidth + LABEL_ROW_GAP + dateWidth;
    const labelWidth = Math.ceil(Math.max(contentWidth, tableLayout?.width ?? 0) + LABEL_OUTER_PADDING_X * 2);
    const labelHeight = Math.ceil(
      LABEL_OUTER_PADDING_Y * 2 + rowHeight + (tableLayout ? LABEL_ROW_GAP + tableLayout.height : 0),
    );

    const hostElement = canvas.parentElement;
    const cssWidth = hostElement?.clientWidth ?? canvas.clientWidth;
    const cssHeight = hostElement?.clientHeight ?? canvas.clientHeight;
    const dpr = window.devicePixelRatio || 1;
    canvas.width = Math.max(1, Math.round(cssWidth * dpr));
    canvas.height = Math.max(1, Math.round(cssHeight * dpr));
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;

    const drawLabel = (dateIconImage?: CanvasImageSource) => {
      const ctx = canvas.getContext("2d");
      if (!ctx) {
        return;
      }

      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      ctx.clearRect(0, 0, cssWidth, cssHeight);

      const outerX = Math.max(0, cssWidth - LABEL_RIGHT_INSET - labelWidth);
      const outerY = LABEL_TOP_INSET;

      drawRoundedRect(
        ctx,
        outerX + 0.5,
        outerY + 0.5,
        labelWidth - 1,
        labelHeight - 1,
        LABEL_BORDER_RADIUS,
        LABEL_BACKGROUND,
        LABEL_BORDER_COLOR,
      );

      const titleX = outerX + LABEL_OUTER_PADDING_X;
      const titleY = outerY + LABEL_OUTER_PADDING_Y;
      const dateX = titleX + titleWidth + LABEL_ROW_GAP;
      const dateY = outerY + LABEL_OUTER_PADDING_Y;

      drawRoundedRect(
        ctx,
        titleX,
        titleY,
        titleWidth,
        rowHeight,
        LABEL_BORDER_RADIUS,
        LABEL_CELL_BACKGROUND,
        LABEL_BORDER_COLOR,
      );
      drawRoundedRect(
        ctx,
        dateX,
        dateY,
        dateWidth,
        rowHeight,
        LABEL_BORDER_RADIUS,
        LABEL_CELL_BACKGROUND,
        LABEL_BORDER_COLOR,
      );

      ctx.save();
      ctx.font = buildFont(titleStyle, titleFontSize);
      ctx.fillStyle = iconColor;
      ctx.textBaseline = "middle";
      const textY = titleY + rowHeight / 2;
      const dateContentX = dateX + LABEL_CELL_PADDING_X;

      ctx.fillText(titleText, titleX + LABEL_CELL_PADDING_X, textY);
      if (dateIconImage) {
        ctx.drawImage(dateIconImage, dateContentX, textY - iconSize / 2, iconSize, iconSize);
      } else {
        drawCalendarIconFallback(ctx, dateContentX, textY, iconSize, iconColor);
      }
      ctx.fillText(combinedDateText, dateContentX + iconSlotWidth, textY);
      ctx.restore();

      if (tableLayout) {
        drawTable(
          ctx,
          outerX + LABEL_OUTER_PADDING_X,
          outerY + LABEL_OUTER_PADDING_Y + rowHeight + LABEL_ROW_GAP,
          tableLayout,
          allInitDates,
          titleStyle,
          i18n.locale,
        );
      }
    };

    const dateIconImage = getCanvasSvgIconImage(DateRangeIcon, { color: iconColor, size: iconSize });
    drawLabel(dateIconImage);

    if (!dateIconImage) {
      void preloadCanvasSvgIconImage(DateRangeIcon, { color: iconColor, size: iconSize }).then((loadedDateIcon) => {
        if (!cancelled) {
          drawLabel(loadedDateIcon);
        }
      });
    }

    return () => {
      cancelled = true;
    };
  }, [allInitDates, displayTime, i18n.locale, map.time_offset_mins, map.title, titleStyle, timezone]);

  if (!displayTime) {
    return null;
  }

  return (
    <canvas
      ref={canvasRef}
      data-testid="map-recording-label-canvas"
      className={SnapshotLabelHtmlClassNames.Map}
      style={{
        position: "absolute",
        inset: 0,
        pointerEvents: "none",
        zIndex: 1,
      }}
    />
  );
}
